<template>
  <div class="parent_cast_cont_">
    <div class="flex flex-col gap-2">
      <div
        class="cast_header flex justify-between items-center w-full h-16 px-4 py-2 z-5 sticky top-0"
      >
        <p class="text-xl font-bold">/record.summary</p>
        <div v-if="summary" class="translate-controls">
          <select
            v-model="selectedLanguage"
            @change="handleLanguageChange"
            class="translate-select"
          >
            <option class="text-green-500" value="">Translate</option>
            <option v-for="lang in languages" :key="lang" :value="lang">
              {{ lang }}
            </option>
          </select>
          <span v-if="isTranslating" class="translating-pill"
            >Translating…</span
          >
        </div>
      </div>
      <div v-if="loading" class="spinner">
        <img
          src="@/assets/images/logot.svg"
          class="spinner-logo"
          alt="decast"
        />
        <p v-if="loading === 'checking'">Checking existing summary...</p>
        <p v-else-if="loading === 'starting'">Starting summary generation...</p>
        <p v-else-if="loading === 'processing'">
          Generating summary... Please wait.
        </p>
      </div>

      <!-- Summary Display -->
      <div v-if="summary && !loading" class="text-white mt-2">
        <div
          class="panel-content formatted-text"
          ref="summaryContainer"
          v-html="formattedSummary"
        ></div>
      </div>

      <!-- Error State -->
      <p v-if="error" class="text-red-500">{{ error }}</p>
    </div>
  </div>
</template>

<script>
import planAccount from '@/mixins/planAccount';

import api from '@/http/api/api.js';

export default {
  mixins: [planAccount],
  name: 'DecastRecordDetails',
  props: ['selectedRecordId', 'recordId'],
  data() {
    return {
      videoUrl: null,
      recording: null,
      speakers: null,
      summary: null,
      recording_id: '',
      public_meeting_id: '',
      loading: false,
      taskId: null,
      pollingInterval: null,
      startedFor: '',
      searchQuery: '',
      searchQuery2: '',
      matchCount: 0,
      matchCount2: 0,
      currentMatchIndex: -1,
      currentMatchIndex2: -1,
      error: null,
      originalSummary: null,
      selectedLanguage: '__original__',
      isTranslating: false,
      languages: [
        'Arabic',
        'Chinese (Simplified)',
        'Chinese (Traditional)',
        'Dutch',
        'French',
        'German',
        'Hindi',
        'Italian',
        'Japanese',
        'Korean',
        'Portuguese',
        'Russian',
        'Spanish',
        'Turkish',
        'Vietnamese',
      ],
      translationCache: {},
    };
  },
  computed: {
    activeRecordId() {
      return this.selectedRecordId || this.recordId || '';
    },
    formattedSummary() {
      return this.formatSummary(this.summary);
    },
  },
  watch: {
    // one recording, one attempt. Both props and mounted() used to call this, so opening a
    // recording asked for the same summary three times over, and under metering three
    // identical requests are three chances to spend an hour that was never needed.
    activeRecordId: {
      immediate: true,
      handler(id) {
        if (id) this.startTranscription();
      },
    },
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    async startTranscription() {
      console.log(
        'Starting transcription for recording_id:',
        this.recordId,
        'selectedRecordId:',
        this.selectedRecordId
      );
      const id = this.activeRecordId;
      if (!id) {
        console.error('No recording ID provided');
        return;
      }
      // a second call for a recording already in hand is a duplicate, not a retry
      if (this.startedFor === id) return;
      this.startedFor = id;
      this.stopPolling();

      this.recording_id = id;
      this.summary = null;
      this.error = null;
      this.loading = 'checking';

      try {
        const checkResponse = await api.get(
          `${this.$globals.transcriptionBaseUrl}/api/audio/transcription/${this.recording_id}/`
        );
        const data = checkResponse.data;

        if (data && data.summary) {
          this.summary = data.summary;
          this.originalSummary = data.summary;
          this.loading = false;
          return;
        }
      } catch (error) {
        console.log('No existing summary found, proceeding with generation.');
      }

      // Reading an existing transcript is never blocked; only making a new one is,
      // and an account with no hours left should be told rather than left spinning.
      if (this.aiBlocked) {
        this.loading = false;
        this.error = this.aiBlockedMessage;
        return;
      }
      // No summary - trigger generation
      this.loading = 'starting';

      try {
        const response = await api.post(
          `${this.$globals.transcriptionBaseUrl}/api/audio/upload/`,
          {
            recordingId: this.recording_id,
            public_meeting_id: '',
          }
        );

        this.taskId = response.data.task_id;
        console.log('Summary generation started with task ID:', this.taskId);
        this.pollTaskStatus();
      } catch (error) {
        console.error('Error starting transcription:', error);
        this.error = 'Failed to start summary generation.';
        this.loading = false;
      }
    },
    async pollTaskStatus() {
      if (!this.taskId) {
        console.error('No task ID available');
        this.loading = false;
        return;
      }

      this.loading = 'processing';
      this.stopPolling();

      this.pollingInterval = setInterval(async () => {
        try {
          const response = await api.get(
            `${this.$globals.transcriptionBaseUrl}/api/audio/get_task_status/${this.taskId}/`
          );
          const { status, result, error } = response.data;

          if (status === 'SUCCESS') {
            this.originalSummary = result.summary || '';
            this.summary = result.summary || 'No summary available.';
            this.stopPolling();
            this.loading = false;
          } else if (status === 'FAILURE') {
            console.error('Task failed:', error);
            this.error = 'Summary generation failed.';
            this.stopPolling();
            this.loading = false;
          }
        } catch (err) {
          console.error('Error polling task status:', err);
          this.error = 'Error while polling summary.';
          this.stopPolling();
          this.loading = false;
        }
      }, 10000);
    },
    formatSummary(text) {
      if (!text) return '';

      //  timestamps
      text = text.replace(
        /\[(\d{2}:\d{2})\]/g,
        '<span class="timestamp text-grey-500 font-semibold cursor-pointer">[$1]</span>'
      );
      return text
        .split('\n')
        .filter((line) => line.trim())
        .map((line) => {
          const trimmed = line.trim();

          // Convert "### Key Points:"
          if (trimmed.startsWith('###')) {
            return `<h3 class="text-lg font-semibold my-4 text-green-500">${trimmed.replace(
              /^###\s*/,
              ''
            )}</h3>`;
          }
          // Convert "**bold text**" inside numbered points to yellow bold
          const formatted = trimmed.replace(
            /\*\*(.+?)\*\*/g,
            '<span class="text-yellow-500 font-bold">$1</span>'
          );

          return `<p class="mb-2">${formatted}</p>`;
        })
        .join('');
    },
    async handleLanguageChange() {
      if (this.selectedLanguage === '__original__') {
        this.summary = this.originalSummary || this.summary;
        return;
      }

      // Use cache if present
      if (this.translationCache[this.selectedLanguage]) {
        this.summary = this.translationCache[this.selectedLanguage];
        return;
      }

      if (!this.originalSummary) return;

      try {
        this.isTranslating = true;
        const { data } = await api.post(
          `${this.$globals.transcriptionBaseUrl}/api/audio/translate/`,
          {
            text: this.originalSummary,
            target_language: this.selectedLanguage,
          }
        );
        const translated = data?.translated || this.originalSummary;
        // cache & set
        this.translationCache[this.selectedLanguage] = translated;
        this.summary = translated;
      } catch (e) {
        console.error('Translation failed:', e);
        // fallback to original on error
        this.selectedLanguage = '__original__';
        this.summary = this.originalSummary;
      } finally {
        this.isTranslating = false;
      }
    },
  },
};
</script>

<style>
.spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  text-align: center;
  animation: blinkOpacity 1.5s infinite ease-in-out;
}

.spinner-logo {
  width: 4rem;
  height: 4rem;
  opacity: 0.8;
}

.spinner p {
  color: #22c55e;
  font-size: 1.1rem;
  margin: 0;
}

.cast_header {
  background-color: #272727;
}

@keyframes blinkOpacity {
  0%,
  100% {
    opacity: 0.8;
  }

  50% {
    opacity: 0.4;
  }
}

.parent_cast_cont_ {
  overflow-y: scroll;
  overflow-x: hidden;
  max-height: 455px;
}

.parent_cast_cont_::-webkit-scrollbar {
  display: none;
}

.translate-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 0.75rem;
  padding: 5px;
}

.translate-select {
  background-color: #111;
  color: #fff;
  border: 1px solid #333;
  padding: 0.4rem 0.6rem;
  font-size: 0.9rem;
}

.translate-select:focus {
  outline: none;
  border-color: #22c55e;
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.3);
}

.translating-pill {
  font-size: 0.8rem;
  color: #22c55e;
  opacity: 0.9;
}
</style>
